import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Radio, Users, Trophy, ShieldCheck } from "lucide-react";
import { IMG, loginTo } from "./data";

function Banner({ img, eyebrow, title, text, cta, to, testId, icon: Icon, flip }) {
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
      className={`grid items-center gap-6 overflow-hidden rounded-3xl border border-border bg-card shadow-sm lg:grid-cols-2 ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <div className="relative h-56 lg:h-full lg:min-h-[280px]">
        <img src={img} alt={title} className="h-full w-full object-cover" />
      </div>
      <div className="p-7 lg:p-10">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 font-mono text-xs uppercase tracking-widest text-primary">
          <Icon className="h-3.5 w-3.5" /> {eyebrow}
        </span>
        <h3 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">{title}</h3>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{text}</p>
        <Link to={loginTo(to)} data-testid={testId}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-colors hover:bg-[#FF8533]">
          {cta} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </motion.div>
  );
}

export default function EcosystemSections() {
  return (
    <>
      <section className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6">
        <Banner img={IMG.tourism} eyebrow="Sports Tourism" icon={Trophy}
          title="Play beyond your city" text="Curated sports trips, training camps and tournaments - travel, stay and compete with everything organised end-to-end."
          cta="Explore trips" to="/app/enquiries" testId="eco-tourism-cta" />
        <Banner flip img={IMG.live} eyebrow="Live Matches" icon={Radio}
          title="Catch the action live" text="Follow fixtures and watch community matches streamed straight from the ground, with scores updated in real time."
          cta="Watch live" to="/app/matches" testId="eco-live-cta" />
      </section>

      <section className="border-t border-border bg-secondary/40">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 py-14 sm:px-6 lg:grid-cols-2">
          <div id="sec-community" className="scroll-mt-24 rounded-3xl border border-border bg-card p-8 shadow-sm">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><Users className="h-6 w-6" /></span>
            <h3 className="mt-4 font-display text-2xl font-bold">Join the community</h3>
            <p className="mt-2 text-sm text-muted-foreground">Connect with players, clubs and academies. Share updates, find teammates and grow your local sports scene in a moderated feed.</p>
            <Link to={loginTo("/app/community")} data-testid="eco-community-cta"
              className="mt-5 inline-flex items-center gap-2 font-semibold text-primary hover:underline">
              Open community <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div id="sec-performance" className="scroll-mt-24 rounded-3xl border border-border bg-card p-8 shadow-sm">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600"><ShieldCheck className="h-6 w-6" /></span>
            <h3 className="mt-4 font-display text-2xl font-bold">Your SPORA ID</h3>
            <p className="mt-2 text-sm text-muted-foreground">Track scores, rankings and certificates with a verifiable SPORA ID and QR - your trusted sporting identity across the ecosystem.</p>
            <Link to={loginTo("/app/performance")} data-testid="eco-performance-cta"
              className="mt-5 inline-flex items-center gap-2 font-semibold text-primary hover:underline">
              View performance <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
